import { Box, IconButton, Tooltip, useDisclosure } from "@chakra-ui/react";
import { RefObject, useEffect, useRef, useState } from "react";
import { RiCloseLine, RiPlayFill } from "react-icons/ri";
import { AudioFile } from "../db";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import { DEFAULT_SAMPLE_LENGTH } from "../Workbench";

interface WaveformPlaybackProps {
  audioFile?: AudioFile;
  onAudioPlay: () => void;
  audioElementRef: RefObject<HTMLAudioElement | null>;
  onDeleteRecording: () => void;
}

const WaveformPlayback = ({
  audioFile,
  onAudioPlay,
  audioElementRef,
  onDeleteRecording,
}: WaveformPlaybackProps) => {
  const [width, setWidth] = useState<number>(0);
  const intervalRef = useRef<any>();
  const isPlaying = useRef<boolean>(false);

  useEffect(() => {
    clearInterval(intervalRef.current);
    setWidth(0);
    isPlaying.current = false;
  }, [audioFile]);

  useEffect(() => {
    const startPlaybackVisualisation = () => {
      if (isPlaying.current) {
        return;
      }
      let internalWidth = 0;
      isPlaying.current = true;
      intervalRef.current = setInterval(() => {
        internalWidth += 1;
        if (internalWidth > 100) {
          clearInterval(intervalRef.current);
          setTimeout(() => {
            setWidth(0);
            isPlaying.current = false;
          }, 100);
        } else {
          setWidth(internalWidth);
        }
      }, (audioFile?.sampleLength || DEFAULT_SAMPLE_LENGTH) / 100);
    };

    const audioElement = audioElementRef.current;
    audioElement?.addEventListener("play", startPlaybackVisualisation);

    return () => {
      audioElement?.removeEventListener("play", startPlaybackVisualisation);
    };
  }, [audioElementRef, audioFile]);

  const deleteRecordingDisclosure = useDisclosure();

  return (
    <>
      <Box
        width={`${width}%`}
        height="150px"
        zIndex={3}
        bgColor="purple.200"
        position="absolute"
        opacity={0.5}
      />
      <Box
        as="img"
        src={audioFile?.waveformImg}
        width="100%"
        height="150px"
        zIndex={2}
        alt="amplitude plot of the recording"
      />
      <IconButton
        disabled={isPlaying.current}
        position="absolute"
        transform="translate(-50%, -50%)"
        left="50%"
        top="50%"
        colorScheme="blue"
        size="lg"
        variant="solid"
        outline="2px solid"
        outlineColor="gray.200"
        isRound={true}
        aria-label="Play recording"
        icon={<RiPlayFill />}
        zIndex={3}
        onClick={onAudioPlay}
      />
      <Tooltip label="Delete recording" placement="top" hasArrow={true}>
        <IconButton
          position="absolute"
          right={0}
          top={0}
          colorScheme="red"
          size="lg"
          variant="ghost"
          aria-label="Delete phrase"
          icon={<RiCloseLine />}
          zIndex={3}
          onClick={deleteRecordingDisclosure.onOpen}
        />
      </Tooltip>
      {deleteRecordingDisclosure.isOpen && (
        <ConfirmDialog
          isOpen={deleteRecordingDisclosure.isOpen}
          onClose={deleteRecordingDisclosure.onClose}
          title={"Delete recording"}
          message="Are you sure you want to delete this recording? This action cannot be undone."
          onConfirm={() => {
            onDeleteRecording();
            deleteRecordingDisclosure.onClose();
          }}
        />
      )}
    </>
  );
};

export default WaveformPlayback;
